import styled from 'styled-components';
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const rows = [
  {
    tab: 'Pedidos',
    problem: 'Comanda perdida, pedido errado, cliente esperando.',
    resolution: 'Pedido certo, da mesa até a cozinha, em tempo real.',
    subtitle: 'O garçom anota na mesa e o pedido vai direto para as telas da cozinha. Fim das comandas ilegíveis, erros de preparo e atrasos. A operação ganha agilidade e o cliente é servido mais rápido.',
  },
  {
    tab: 'Caixa',
    problem: 'Fechar o caixa tarde da noite sem saber onde foi o furo.',
    resolution: 'Caixa fechado em minutos, com cada centavo rastreado.',
    subtitle: 'Cada venda, desconto ou cancelamento é registrado automaticamente. O fechamento do turno leva apenas alguns minutos, garantindo que cada centavo seja rastreado e evitando perdas invisíveis.',
  },
  {
    tab: 'Equipe',
    problem: 'Equipe nova, prejuízo de novo até pegar o jeito.',
    resolution: 'Time pronto pra operar desde o primeiro turno.',
    subtitle: 'Com um sistema intuitivo e fácil de usar, o treinamento de novos funcionários é muito rápido. Sua equipe consegue operar o sistema com confiança e sem travar o atendimento logo no primeiro turno.',
  },
  {
    tab: 'Clientes',
    problem: 'Cliente vem uma vez e você nunca mais ouve falar dele.',
    resolution: 'Promoções certeiras e feedback direto pra ele voltar.',
    subtitle: 'Construa um histórico de consumo do seu público para criar campanhas direcionadas. Use dados para enviar promoções, programas de fidelidade e garantir que o cliente sempre volte ao seu restaurante.',
  },
  {
    tab: 'Crescimento',
    problem: 'Crescer de uma unidade pra dez sem perder o controle de nada.',
    resolution: 'O mesmo painel, do primeiro balcão ao centésimo.',
    subtitle: 'Administre o estoque, o cardápio e o faturamento de várias unidades (seja uma ou dezenas) através de um único painel na nuvem. Tenha o controle total do seu negócio de qualquer lugar.',
  },
];

const AUTO_ADVANCE_MS = 5000;

const Section = styled.section`
  position: relative;
  width: 100%;
  margin: 2rem auto;
  padding: 7rem 2rem;
  overflow: hidden;
  background: linear-gradient(135deg, var(--color-brand), var(--color-brand), var(--color-brand-deep));

  @media screen and (max-width: 768px) {
    width: 100%;
    border-radius: 0px;
    padding: 5rem 1.5rem;
  }
`;

const Layout = styled.div`
  position: relative;
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 4rem;
  align-items: center;

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const Aside = styled.div``;

const Eyebrow = styled.p`
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-accent-soft);
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(1.9rem, 3vw, 2.5rem);
  font-weight: 600;
  line-height: 1.15;
  color: var(--color-white);
  margin-bottom: 2.5rem;
`;

const TabList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(255, 255, 255, 0.14);

  @media screen and (max-width: 900px) {
    flex-direction: row;
    flex-wrap: wrap;
    border-top: none;
    gap: 0.6rem;
  }
`;

const TabItem = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 0;
  background: none;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  cursor: pointer;
  text-align: left;

  @media screen and (max-width: 900px) {
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 999px;
    padding: 0.5rem 1.1rem;
  }
`;

const TabNumber = styled.span`
  font-family: var(--font-body);
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ $active }) => ($active ? 'var(--color-accent-soft)' : 'rgba(255, 255, 255, 0.4)')};

  @media screen and (max-width: 900px) {
    display: none;
  }
`;

const TabLabel = styled.span`
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 600;
  color: ${({ $active }) => ($active ? 'var(--color-white)' : 'rgba(255, 255, 255, 0.45)')};
  transition: color 0.25s ease;

  @media screen and (max-width: 900px) {
    font-size: 0.9rem;
  }
`;

const Stage = styled(motion.div)`
  position: relative;
  min-height: 260px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media screen and (max-width: 900px) {
    min-height: 200px;
  }
`;

const StageContent = styled(motion.div)``;

const ProblemText = styled.p`
  font-family: var(--font-body);
  font-size: 1.15rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 1.25rem;
`;

const ResolutionText = styled.p`
  font-family: var(--font-display);
  font-size: clamp(1.7rem, 3vw, 2.3rem);
  font-weight: 600;
  line-height: 1.25;
  color: var(--color-white);
`;

const SubText = styled.p`
  font-family: var(--font-body);
  font-size: 1.15rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 0.75rem;
`;

const ProgressTrack = styled.div`
  margin-top: 2.5rem;
  height: 2px;
  width: 100%;
  max-width: 260px;
  background: rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: var(--color-brand-light);
`;

// O react-snap (prerendering, ver Fase 5 da spec) roda a página inteira num
// browser real antes de capturar o HTML estático. Se o timer de auto-avanço
// rodasse durante essa captura, o HTML gravado poderia ficar num índice
// diferente de 0 — e a hidratação no cliente (que sempre começa do zero)
// acusaria erro de mismatch (React #418/#425). O react-snap marca a própria
// aba com esse user agent para permitir esse tipo de guarda.
const isPrerendering = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const PainList = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (isPaused || isPrerendering) return undefined;

    timeoutRef.current = setTimeout(() => {
      setActiveIndex((current) => (current + 1) % rows.length);
    }, AUTO_ADVANCE_MS);

    return () => clearTimeout(timeoutRef.current);
  }, [activeIndex, isPaused]);

  const active = rows[activeIndex];

  return (
    <Section
      id="pain-list"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <Layout>
        <Aside>
          <Eyebrow>Do jeito que é, hoje</Eyebrow>
          <Title>Os problemas do dia a dia, resolvidos.</Title>

          <TabList>
            {rows.map((row, index) => (
              <TabItem
                key={row.tab}
                onClick={() => setActiveIndex(index)}
                aria-current={index === activeIndex}
              >
                <TabNumber $active={index === activeIndex}>{String(index + 1).padStart(2, '0')}</TabNumber>
                <TabLabel $active={index === activeIndex}>{row.tab}</TabLabel>
              </TabItem>
            ))}
          </TabList>
        </Aside>

        <Stage layout>
          <AnimatePresence mode="wait" initial={false}>
            <StageContent
              key={activeIndex}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <ProblemText>{active.problem}</ProblemText>
              <ResolutionText>{active.resolution}</ResolutionText>
              <SubText>{active.subtitle ?? ''}</SubText>
            </StageContent>
          </AnimatePresence>

          <ProgressTrack>
            <ProgressFill
              key={activeIndex}
              initial={{ width: '0%' }}
              animate={{ width: isPaused ? undefined : '100%' }}
              transition={{ duration: AUTO_ADVANCE_MS / 1000, ease: 'linear' }}
            />
          </ProgressTrack>
        </Stage>
      </Layout>
    </Section>
  );
};

export default PainList;
